import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardCopy,
  Download,
  Eye,
  FolderKanban,
  FolderInput,
  MoreHorizontal,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import type { Asset } from '@mg/shared';
import { cn } from '../../lib/cn';
import { useDismissable } from '../../lib/useDismissable';
import { formatRelative } from '../../lib/display';
import { Badge } from '../../components/ui/Badge';
import { useDialogs } from '../../components/ui/DialogProvider';
import { MediaThumb } from './MediaThumb';
import {
  assetName,
  downloadAsset,
  formatBytes,
  fileTypeLabel,
  sourceBadge,
} from './mediaDisplay';

export interface MediaCardActions {
  onOpen: (asset: Asset) => void;
  onMove: (asset: Asset) => void;
  onDelete: (asset: Asset) => void;
  onRestore: (asset: Asset) => void;
  /** Omitted where bulk selection doesn't apply (the trash view) — then no checkbox renders. */
  onToggleSelect?: (asset: Asset) => void;
}

/**
 * A single Media Library asset. The thumbnail opens the viewer; the kebab menu
 * carries Open / Download / Move / Copy prompt / Delete (or Restore in the trash
 * view). Uploaded vs AI-generated is shown as a badge; the meta line reads
 * type · size · relative date, with a chip per associated campaign.
 *
 * With `onToggleSelect` wired, a checkbox sits over the thumbnail for bulk actions. It is always
 * rendered — hover-to-reveal would make the very first selection undiscoverable on a touch device,
 * where there is no hover — but stays muted until it is hovered, focused, or ticked.
 *
 * Selection is MODAL. With nothing selected the tile behaves normally: the thumbnail opens the
 * viewer, and only the checkbox starts a selection. Once anything is selected (`anySelected`) the
 * whole tile becomes the toggle, so a run of images is picked with single clicks instead of
 * hitting each small checkbox — and the preview is suppressed, since opening a modal is not what
 * a click means while you are picking. Clearing the selection restores the normal behaviour.
 */
export function MediaCard({
  asset,
  isTrash,
  selected = false,
  anySelected = false,
  actions,
}: {
  asset: Asset;
  isTrash: boolean;
  selected?: boolean;
  anySelected?: boolean;
  actions: MediaCardActions;
}) {
  const badge = sourceBadge(asset);
  const campaign = asset.campaigns[0];
  const extraCampaigns = asset.campaigns.length - 1;
  // Generating / failed assets have no viewable blob yet — the tile isn't clickable.
  const isReady = asset.status === 'ready';

  const thumbInner = (
    <>
      <MediaThumb asset={asset} className="h-full w-full" />
      {/* Top-RIGHT so it never sits under the bulk-selection checkbox in the top-left. */}
      <span className="absolute right-2 top-2">
        <Badge tone={badge.tone}>{badge.label}</Badge>
      </span>
    </>
  );

  const selectable = Boolean(actions.onToggleSelect);
  // Selection mode: the tile toggles instead of opening, and the preview is off. Deliberately keyed
  // off `anySelected` (the grid) rather than `selected` (this card) — the point is that the SECOND
  // and subsequent picks are one click anywhere, once the first has been made via the checkbox.
  const selecting = selectable && anySelected;
  // Non-ready tiles have no blob to preview, so they're inert normally — but they still carry a
  // checkbox, so while selecting they must be clickable like any other tile.
  const tileClickable = isReady || selecting;

  return (
    <div
      className={cn(
        'group relative flex flex-col rounded-xl border bg-card shadow-card',
        selected ? 'border-action ring-2 ring-action/20' : 'border-line',
      )}
    >
      {selectable && (
        // Sits above the thumbnail button rather than inside it — nesting an input in a button
        // is invalid, and the click would open the viewer instead of toggling.
        <label
          className={cn(
            'absolute left-2 top-2 z-10 flex cursor-pointer items-center rounded-md bg-card/90 p-1 shadow-sm backdrop-blur transition-opacity',
            selected || anySelected
              ? 'opacity-100'
              : 'opacity-60 focus-within:opacity-100 group-hover:opacity-100',
          )}
        >
          <input
            type="checkbox"
            checked={selected}
            onChange={() => actions.onToggleSelect?.(asset)}
            className="h-4 w-4 rounded border-line-input"
            aria-label={`Select ${assetName(asset)}`}
          />
        </label>
      )}

      {/* ALWAYS a button, disabled when there's nothing to do. Swapping the element type between
          <button> and <div> as selection mode flips would unmount the node the user is standing
          on: deselecting the last item from a generating tile dropped keyboard focus to <body>
          and sent the next Tab back to the top of the page. */}
      <button
        type="button"
        disabled={!tileClickable}
        onClick={() => (selecting ? actions.onToggleSelect?.(asset) : actions.onOpen(asset))}
        // The name tracks the MODE, not the tick: a toggle button should keep a stable name and
        // let aria-pressed carry its state, or a screen reader announces the change twice.
        aria-label={selecting ? `Select ${assetName(asset)}` : `Open ${assetName(asset)}`}
        aria-pressed={selecting ? selected : undefined}
        className="focus-ring relative block aspect-[4/3] w-full overflow-hidden rounded-t-xl disabled:cursor-default"
      >
        {thumbInner}
      </button>

      <div className="flex min-w-0 flex-1 items-start justify-between gap-2 px-3 py-2.5">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-ink" title={assetName(asset)}>
            {assetName(asset)}
          </h3>
          <p className="mt-0.5 truncate text-xs text-ink-muted">
            {fileTypeLabel(asset)} · {formatBytes(asset.sizeBytes)} ·{' '}
            {formatRelative(asset.createdAt)}
          </p>
          {campaign && (
            <Link
              to={`/campaigns/${campaign.id}`}
              className="focus-ring mt-1.5 inline-flex max-w-full items-center gap-1 rounded text-xs font-medium text-link hover:text-action-hover hover:underline"
              title={campaign.name}
            >
              <FolderKanban className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="truncate">
                in {campaign.name}
                {extraCampaigns > 0 ? ` +${extraCampaigns}` : ''}
              </span>
            </Link>
          )}
        </div>

        <MediaCardMenu asset={asset} isTrash={isTrash} selecting={selecting} actions={actions} />
      </div>
    </div>
  );
}

function MediaCardMenu({
  asset,
  isTrash,
  selecting,
  actions,
}: {
  asset: Asset;
  isTrash: boolean;
  /** Bulk selection is active — the preview is suppressed everywhere, this menu included. */
  selecting: boolean;
  actions: MediaCardActions;
}) {
  const { alert } = useDialogs();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useDismissable(open, ref, () => setOpen(false));

  // Only ready assets have a blob to open / download / move; generating + failed tiles
  // expose Delete only (Restore in the trash view).
  const isReady = asset.status === 'ready';
  const canCopyPrompt = asset.source === 'generation' && Boolean(asset.prompt?.trim());

  const itemCls =
    'focus-ring flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink-strong hover:bg-hover';

  const run = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  const doDownload = () => {
    void downloadAsset(asset).catch(() =>
      void alert({
        title: 'Something went wrong',
        message: 'Could not download this file — please try again.',
      }),
    );
  };

  const doCopyPrompt = () => {
    if (asset.prompt) void navigator.clipboard?.writeText(asset.prompt);
  };

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="focus-ring rounded-md p-1.5 text-ink-faint hover:bg-chip hover:text-ink-strong"
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-40 mt-1 w-48 overflow-hidden rounded-lg border border-line bg-card py-1 shadow-panel"
        >
          {isReady && (
            <>
              {/* Hidden while selecting: the preview is off for the whole card in that mode, and
                  leaving one way in here would make the rule look like a bug on the thumbnail. */}
              {!selecting && (
                <button type="button" role="menuitem" className={itemCls} onClick={() => run(() => actions.onOpen(asset))}>
                  <Eye className="h-4 w-4 text-ink-faint" aria-hidden />
                  Open to view
                </button>
              )}
              <button type="button" role="menuitem" className={itemCls} onClick={() => run(doDownload)}>
                <Download className="h-4 w-4 text-ink-faint" aria-hidden />
                Download
              </button>
              <button type="button" role="menuitem" className={itemCls} onClick={() => run(() => actions.onMove(asset))}>
                <FolderInput className="h-4 w-4 text-ink-faint" aria-hidden />
                Move to folder…
              </button>
              {canCopyPrompt && (
                <button type="button" role="menuitem" className={itemCls} onClick={() => run(doCopyPrompt)}>
                  <ClipboardCopy className="h-4 w-4 text-ink-faint" aria-hidden />
                  Copy prompt
                </button>
              )}
              <div className="my-1 border-t border-line-soft" />
            </>
          )}
          {isTrash ? (
            <button
              type="button"
              role="menuitem"
              className="focus-ring flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-action hover:bg-tint hover:text-tint-fg"
              onClick={() => run(() => actions.onRestore(asset))}
            >
              <RotateCcw className="h-4 w-4" aria-hidden />
              Restore
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              className="focus-ring flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 dark:text-error-dark hover:bg-red-50 dark:hover:bg-error-dark-tint"
              onClick={() => run(() => actions.onDelete(asset))}
            >
              <Trash2 className="h-4 w-4" aria-hidden />
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}
